import { BaseAPI } from '@/infrastructure/api/client_typescript/base';
import { Configuration } from '@/infrastructure/api/client_typescript';
import type { TokenPair } from '@/infrastructure/api/client_typescript';
import { UserApi } from '@/infrastructure/api/client_typescript';
import { reloadOnceOnly } from '@/interfaces/utils/session';
import { TokenStore } from '@/infrastructure/storage/TokenStore';

class TokenNotAvailableError extends Error {}

export class SessionManager extends BaseAPI {
  private tokens: TokenPair | null = null;
  private sessionInitialized: boolean = false;
  private initializationPromise!: Promise<void>;
  private refreshInProgress: Promise<boolean> | null = null;
  private tokenReadyPromise: Promise<void> | null = null;
  private tokensApi: UserApi;
  private tokenCheckIntervalMs = 60 * 1000; // Check tokens every 1 minute
  private store: TokenStore;
  private periodicChecksStarted = false;

  constructor(configuration: Configuration, store: TokenStore) {
    // accessToken is a function so it always reads latest memory/store value
    super(
      new Configuration({
        ...configuration,
        accessToken: () => this.tokens?.access_token || store.getAccess() || '',
      })
    );
    this.store = store;
    this.tokensApi = new UserApi(this.configuration);
    this.loadTokens();
  }

  public async initializeSession(): Promise<void> {
    // Return existing promise if initialization is in progress or complete
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = (async () => {
      try {
        // If we have a valid access token, we're good
        if (this.hasValidAccessToken()) {
          this.sessionInitialized = true;
        }
        // Try to refresh if we have a refresh token
        else if (this.hasValidRefreshToken()) {
          const refreshed = await this.tryRefreshTokens();
          if (refreshed && this.hasValidAccessToken()) {
            this.sessionInitialized = true;
          } else {
            this.clearAllTokens();
            // No valid session, but that's OK - user is not authenticated
            this.sessionInitialized = true;
          }
        }
        // No tokens at all - user is not authenticated, but app can still work
        else {
          this.sessionInitialized = true;
        }
      } catch {
        this.clearAllTokens();
        // Even on error, mark as initialized so app can continue
        this.sessionInitialized = true;
      }

      if (this.sessionInitialized) {
        // Do not remove 'session_reloaded' here — it is used as a one-time guard
        // to avoid reload loops. Start periodic checks only if we have a
        // refresh token available (prevents unauthenticated tabs from
        // scheduling reload checks).
        if (this.tokens?.refresh_token) {
          this.startPeriodicChecks();
        }
      }
    })();

    await this.initializationPromise;
  }

  private startPeriodicChecks(): void {
    if (this.periodicChecksStarted) return;
    this.periodicChecksStarted = true;

    setInterval(() => this.checkAndRefreshTokens(), this.tokenCheckIntervalMs);

    // Refresh tokens when the page becomes visible again (e.g. after sleep/tab switch).
    // Stores the promise so makeAuthenticatedRequest can wait for it.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        // Only attempt a visibility-triggered refresh if we actually have
        // a refresh token present. If there are no tokens (unauthenticated
        // user), don't trigger a refresh or a reload when returning to the tab.
        if (
          !document.hidden &&
          this.tokens?.refresh_token &&
          !this.hasValidAccessToken()
        ) {
          this.tokenReadyPromise = this.checkAndRefreshTokensWithRetry().finally(
            () => {
              this.tokenReadyPromise = null;
            }
          );
        }
      });
    }
  }

  private async checkAndRefreshTokensWithRetry(
    retries = 3,
    delayMs = 1500
  ): Promise<void> {
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        await this.checkAndRefreshTokens();
        return;
      } catch {
        if (attempt < retries - 1) {
          await this.delay(delayMs);
        }
      }
    }
  }

  private async checkAndRefreshTokens(): Promise<void> {
    if (!this.hasValidRefreshToken()) {
      reloadOnceOnly();
      return;
    }

    // Single refresh path per tick (avoid double refresh calls)
    const needRefresh =
      this.isTokenExpiringSoon(this.tokens?.access_token) ||
      this.isTokenExpiringSoon(this.tokens?.refresh_token);
    if (needRefresh) {
      const refreshed = await this.tryRefreshTokens();
      if (!refreshed) {
        throw new Error('Token refresh failed');
      }
    }
  }

  public isInitialized(): boolean {
    return this.sessionInitialized;
  }

  /**
   * Re-sync in-memory tokens from the TokenStore (localStorage).
   * Call this after external code (e.g. sign-in) writes new tokens to the store
   * so that the SessionManager uses the up-to-date refresh token.
   */
  public reloadTokensFromStore(): void {
    this.loadTokens();
    // If we just loaded tokens after a login/sign-in, ensure periodic
    // checks are started so refresh logic runs in the background.
    if (this.tokens?.refresh_token && this.sessionInitialized) {
      this.startPeriodicChecks();
    }
  }

  public getActualAccessToken(): string {
    if (!this.isInitialized()) {
      throw new TokenNotAvailableError('Session not initialized yet');
    }
    const accessToken = this.tokens?.access_token;
    if (!accessToken) {
      throw new TokenNotAvailableError('Access token not available');
    }
    return accessToken;
  }

  public async makeAuthenticatedRequest<T>(
    requestFunc: () => Promise<T>
  ): Promise<T> {
    await this.ensureSessionInitialized();
    // Wait for any visibility-triggered token refresh to complete
    // before sending the request with a potentially stale token
    if (this.tokenReadyPromise) {
      await this.tokenReadyPromise;
    }
    // Pre-flight: if access token is expired, refresh before sending
    if (!this.hasValidAccessToken()) {
      if (this.refreshInProgress) {
        await this.refreshInProgress;
      } else if (this.hasValidRefreshToken()) {
        await this.tryRefreshTokens();
      } else {
        // No valid tokens — throw to let caller handle (e.g., show login)
        throw new Error('Session expired');
      }
    }
    try {
      return await requestFunc();
    } catch (err: unknown) {
      const error = err as { response?: { status?: number }; code?: string };
      if (error.response?.status === 401) {
        if (this.hasValidRefreshToken()) {
          this.clearAccessToken();
          const refreshed = await this.tryRefreshTokens();
          if (refreshed) {
            return await requestFunc();
          }
        }
        // Session expired - throw to let caller handle
        throw new Error('Session expired');
      }
      // Retry once on transient server/network errors (e.g. 502 after wake from sleep)
      if (this.isTransientError(error)) {
        await this.delay(1500);
        return await requestFunc();
      }
      throw error;
    }
  }

  private isTransientError(error: {
    response?: { status?: number };
    code?: string;
  }): boolean {
    const status = error.response?.status;
    return (
      status === 502 ||
      status === 503 ||
      status === 504 ||
      error.code === 'ERR_NETWORK'
    );
  }

  private async ensureSessionInitialized(): Promise<void> {
    if (!this.sessionInitialized) {
      if (!this.initializationPromise) {
        await this.initializeSession(); // CHANGED: kick it off if not started
      }
      await this.initializationPromise;
    }
  }

  private async tryRefreshTokens(): Promise<boolean> {
    // If a refresh is already in progress, share the same promise
    // to prevent concurrent refresh calls from racing
    if (this.refreshInProgress) {
      return this.refreshInProgress;
    }

    this.refreshInProgress = this.doRefreshTokens();
    try {
      return await this.refreshInProgress;
    } finally {
      this.refreshInProgress = null;
    }
  }

  private async doRefreshTokens(): Promise<boolean> {
    if (!this.tokens?.refresh_token) return false;

    try {
      const response = await this.tokensApi.refreshTokens(
        { refresh: this.tokens.refresh_token },
        {
          // Remove Authorization header to prevent sending expired access token
          // The refresh endpoint should not require bearer auth
          headers: { Authorization: undefined },
        }
      );
      // Be tolerant to different response shapes
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const access =
        (response.data as any).access ||
        (response.data as any).access_token ||
        (response.data as any)?.tokens?.access_token;

      // Keep old refresh unless backend rotates it
      const refresh =
        (response.data as any).refresh ||
        (response.data as any).refresh_token ||
        this.tokens.refresh_token;
      /* eslint-enable @typescript-eslint/no-explicit-any */

      if (!access) {
        return false;
      }

      this.tokens = {
        access_token: access,
        refresh_token: refresh,
      };
      this.saveTokens();
      return true;
    } catch (err: unknown) {
      const error = err as { response?: { status?: number } };
      if (error.response?.status === 401) {
        this.clearAllTokens();
        reloadOnceOnly();
      }
      return false;
    }
  }

  private hasValidAccessToken(): boolean {
    return (
      !!this.tokens?.access_token &&
      !this.isTokenExpired(this.tokens.access_token)
    );
  }

  private hasValidRefreshToken(): boolean {
    // Treat opaque refresh tokens as valid if present
    const rt = this.tokens?.refresh_token;
    if (!rt) return false;
    // If it's a JWT, check expiry; otherwise assume valid.
    return !this.isTokenExpiringSoon(rt);
  }

  private isTokenExpired(token: string): boolean {
    try {
      const parts = token.split('.');
      // opaque token → can't decode → assume not expired
      if (parts.length < 2) return false;
      const payload = JSON.parse(atob(parts[1]));
      const expiryTime = payload.exp * 1000;
      const currentTime = Date.now();
      return expiryTime <= currentTime || payload.invalid === true;
    } catch {
      return false;
    }
  }

  private isTokenExpiringSoon(token?: string): boolean {
    if (!token) return true;
    try {
      const parts = token.split('.');
      if (parts.length < 2) return false;
      const payload = JSON.parse(atob(parts[1]));
      const expiryTime = payload.exp * 1000;
      const currentTime = Date.now();
      const timeLeft = expiryTime - currentTime;
      return timeLeft < 60 * 1000;
    } catch {
      return false;
    }
  }

  private getAuthorizationHeader(): { [key: string]: string } {
    const accessToken = this.tokens?.access_token || '';
    return { Authorization: `Bearer ${accessToken}` };
  }

  private saveTokens(): void {
    if (this.tokens) {
      this.store.setTokens(
        this.tokens.access_token || '',
        this.tokens.refresh_token || ''
      );
    } else {
      this.store.clearAll();
    }
  }

  private loadTokens(): void {
    const access = this.store.getAccess();
    const refresh = this.store.getRefresh();
    if (access) {
      this.tokens = {
        access_token: access,
        refresh_token: refresh || '',
      };
    } else {
      this.tokens = null;
    }
  }

  private clearAccessToken(): void {
    if (this.tokens) {
      this.tokens.access_token = '';
    }
    this.store.clearAccess();
  }

  private clearAllTokens(): void {
    this.tokens = null;
    this.store.clearAll();
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}